import React from 'react';
import { AiFillPoundCircle, AiFillSafetyCertificate } from 'react-icons/ai';
import { BsBuildingFillAdd, BsRocketTakeoffFill } from 'react-icons/bs';
import { FaGlobeEurope, FaRegHandshake } from 'react-icons/fa';
import { FaArrowTrendUp, FaRankingStar } from 'react-icons/fa6';
import { MdOutlineEnergySavingsLeaf, MdOutlineLaptopChromebook, MdOutlineSavings } from 'react-icons/md';
import { PiLeafFill } from 'react-icons/pi';
import { RiTeamFill } from 'react-icons/ri';
import { ColorfulHorizontalLine } from '@epcbuilder/lib/components/Lines';
import PageSection from '../PageSection';

const WhoWeArePage = () => {
  return (
    <div className="flex min-h-screen flex-col gap-10 bg-white lg:gap-12 xl:gap-16">
      <div id="finance-intro" className="mx-6 mt-10 md:mx-6 md:mt-12 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl">
        <h2 className="text-dark text-md text-2xl font-bold leading-tight md:text-[48px] xl:text-[64px]">Our Story</h2>
      </div>
      <PageSection
        title="Who We Are"
        containerClassName="sm:mx-8 mx-6 md:mx-6 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl"
      >
        <div className="flex flex-col gap-4 leading-[30px] xl:gap-6 xl:leading-9">
          <p className="leading-[30px] xl:leading-9">
            EPC Builder was created by Improveasy, a market leader in the home improvement sector. Founded in 2011 by
            current CEO Austin Barcley, Improveasy was built on a vision to make a positive environmental impact while
            helping homeowners access energy-efficient upgrades through government-backed grant schemes.
          </p>
          <ul className="list-inside list-disc">
            <p className="leading-[30px] xl:leading-9">Over the years, Improveasy has expanded into:</p>
            <li className="leading-[30px] xl:leading-9">An installations business</li>
            <li className="leading-[30px] xl:leading-9">A credit broker, offering a range of financial solutions</li>
            <li className="leading-[30px] xl:leading-9">A managing agent for government grant schemes</li>
            <p className="leading-[30px] xl:leading-9">
              EPC Builder brings these services together into an online one-stop shop, guiding property owners through
              their retrofit journey with ease.
            </p>
          </ul>
        </div>
      </PageSection>
      <div className="bg-white md:bg-[#F8F9FA]">
        <div className="flex flex-col gap-6 md:flex-row md:items-center">
          <div className="px-6 md:w-1/2 md:px-0">
            <img src="/images/who-we-are.jpeg" alt="Our Mission" className="w-full rounded-[10px] md:rounded-[0px]" />
          </div>
          <div className="mx-6 sm:mx-0 md:w-1/2">
            <h2 className="mb-4 mr-6 font-[unset] text-lg font-black uppercase md:text-[22px] xl:text-2xl">
              Our mission
            </h2>
            <ColorfulHorizontalLine width="10rem" />
            <p className="mr-6 mt-4 leading-[30px] md:max-w-[552px] xl:leading-9">
              To provide efficient and cost-effective home improvement solutions for property owners. We aim to help
              householders save money and energy by offering innovative and sustainable upgrades tailored to their
              needs.
            </p>
          </div>
        </div>
      </div>

      <PageSection
        title="Our Values"
        containerClassName="sm:mx-8 mx-6 md:mx-6 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: <FaRegHandshake size={30} />,
              title: 'Personalised Approach',
              text: 'We recognise that every home is unique, and we tailor our services to meet the specific needs and preferences of each customer.',
            },
            {
              icon: <MdOutlineSavings size={30} />,
              title: 'Affordable Solutions',
              text: 'We understand the importance of budget-friendly options, and we strive to provide cost-effective solutions without compromising on quality or efficiency.',
            },
            {
              icon: <MdOutlineEnergySavingsLeaf size={30} />,
              title: 'Energy Efficiency',
              text: 'We are committed to helping homeowners upgrade their homes in order to reduce energy consumption and Co2 emissions.',
            },
          ].map(({ icon, title, text }) => (
            <div key={title} className="flex flex-col items-center text-center">
              <div className="relative flex size-12 items-center justify-center rounded-full">
                <div
                  className="absolute inset-0 rounded-full p-[2px]"
                  style={{
                    background:
                      'conic-gradient(#0D692C, #2B9148, #80C066, #E9E669, #E9C469, #F4A261, #E76F51, #0D692C)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    borderRadius: '9999px',
                  }}
                />
                <span className="relative z-10 text-[#85979E]">{icon}</span>
              </div>
              <h4 className="my-4 text-lg font-semibold text-[#002C3E]">{title}</h4>
              <p className="leading-[30px] xl:leading-9">{text}</p>
            </div>
          ))}
        </div>
      </PageSection>
      <PageSection
        title="Our History"
        containerClassName="sm:mx-8 mx-6 md:mx-6 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl"
      >
        <div className="relative mx-auto w-full">
          <div
            className="absolute left-[0.1rem] top-4 z-10 h-[99%] w-[4px] -translate-x-1/2 sm:left-1/2 sm:top-9 sm:h-[95%]"
            style={{
              background: `linear-gradient(to bottom, #E87452, #EC8357, #F2A862, #EEB565, #EABF68, #AEB774, #60A884, #2A9C8E, #1D8577, #0F6D60)`,
            }}
          />

          {[
            {
              year: '2011',
              icon: <PiLeafFill size={30} />,
              text: 'Improveasy founded by Austin Barcley',
              color: '#E87452',
              side: 'right',
            },
            {
              year: '2015',
              icon: <AiFillSafetyCertificate size={30} />,
              text: 'Regulated by the Financial Conduct Authority (FCA)',
              color: '#EC8357',
              side: 'left',
            },
            {
              year: '2019',
              icon: <FaRankingStar size={30} />,
              text: 'Featured in The Sunday Times Fast Track 100',
              color: '#F2A862',
              side: 'right',
            },
            {
              year: '2020',
              icon: <BsRocketTakeoffFill size={30} />,
              text: 'Recognised again in The Sunday Times Fast Track 100',
              color: '#EEB565',
              side: 'left',
            },
            {
              year: '2021',
              icon: <FaGlobeEurope size={30} />,
              text: 'Listed in Financial Times Top 1000 Europe’s Fastest Growing Companies',
              color: '#EABF68',
              side: 'right',
            },
            {
              year: '2022',
              icon: <FaArrowTrendUp size={30} />,
              text: 'Listed again in Financial Times Top 1000 Europe’s Fastest Growing Companies',
              color: '#AEB774',
              side: 'left',
            },
            {
              year: '2023',
              icon: <BsBuildingFillAdd size={30} />,
              text: 'Expanded into a new 5,000 sq ft office and 15,000 sq ft warehouse',
              color: '#60A884',
              side: 'right',
            },
            {
              year: '2024',
              icon: <AiFillPoundCircle size={30} />,
              text: 'Achieved £40m+ turnover',
              color: '#2A9C8E',
              side: 'left',
            },
            {
              year: '2024',
              icon: <RiTeamFill size={30} />,
              text: 'Grew to 100+ full-time employees',
              color: '#1D8577',
              side: 'right',
            },
            {
              year: '2024',
              icon: <MdOutlineLaptopChromebook size={30} />,
              text: 'Launched the innovative EPC Builder online service',
              color: '#0F6D60',
              side: 'left',
            },
          ].map(({ year, icon, text, color }, index) => {
            const isEven = index % 2 !== 0;
            return (
              <div key={index} className="relative mb-16 mt-6 flex w-full flex-col items-center sm:flex-row">
                <div className="flex w-full items-start sm:justify-center">
                  <div className="mt-4 w-full sm:hidden">
                    <div
                      className="absolute top-[-0.75rem] z-10 rounded-br-[30px] rounded-tl-[30px] bg-[#E87452] p-4 font-bold text-white"
                      style={{ backgroundColor: color }}
                    >
                      {year}
                    </div>
                    <div className="relative rounded-md bg-[#F8F9FA] p-4 pl-6">
                      <div className="mb-2 flex justify-center text-[#85979E]">{icon}</div>
                      <p className="font-jost text-xl leading-6">{text}</p>
                    </div>
                  </div>
                  <div className="hidden w-full sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                    {isEven ? (
                      <>
                        <div className="flex justify-end">
                          <div className="w-full max-w-xl rounded-md bg-[#F8F9FA] p-4">
                            <p className="font-jost text-xl lg:text-[24px] lg:leading-10">{text}</p>
                          </div>
                        </div>
                        <div className="relative z-10 flex items-center justify-center">
                          <div
                            className="z-10 flex size-[67px] items-center justify-center rounded-full border-[3px] bg-white text-xl"
                            style={{ color }}
                          >
                            <div className="text-[#85979E]">{icon}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0">
                          <div className="h-[3px] w-[48px]" style={{ backgroundColor: color }} />
                          <div
                            className="font-jost ml-0 flex h-[67px] w-[156px] items-center justify-center rounded-[0px_30px] px-8 py-4 text-2xl font-bold text-white"
                            style={{ backgroundColor: color }}
                          >
                            {year}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-end gap-0">
                          <div
                            className="font-jost mr-0 flex h-[67px] w-[156px] items-center justify-center rounded-[30px_0px] px-8 py-4 text-2xl font-bold text-white"
                            style={{ backgroundColor: color }}
                          >
                            {year}
                          </div>
                          <div className="h-[3px] w-[48px]" style={{ backgroundColor: color }} />
                        </div>
                        <div className="relative z-10 flex items-center justify-center">
                          <div
                            className="z-10 flex size-[67px] items-center justify-center rounded-full border-[3px] bg-white text-xl"
                            style={{ color }}
                          >
                            <div className="text-[#85979E]">{icon}</div>
                          </div>
                        </div>
                        <div className="flex justify-start">
                          <div className="w-full max-w-xl rounded-md bg-[#F8F9FA] p-4">
                            <p className="font-jost text-xl lg:text-[24px] lg:leading-10">{text}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PageSection>
    </div>
  );
};

export default WhoWeArePage;
